import { $, $$, setHTML, icons, statusLabel, esc, isValidTCKN, isValidVKN, setBigTitle, settingsLabel } from './helpers';
import { api } from './api';
import { toast, closeModal } from './ui';
import { openCommandPalette, closeCommandPalette } from './cmd-palette';
import { renderDashboard } from './pages/dashboard';
import { renderClients, renderClientDetail } from './pages/clients';
import { renderProjects, renderProjectDetail } from './pages/projects';
import { renderInvoices, renderInvoiceDetail } from './pages/invoices';
import { renderPayments } from './pages/payments';
import { renderActivity, renderAnnualReport, renderSettings } from './pages/other';
import { renderDocuments } from './pages/documents';
import { renderTickets, renderTicketDetail } from './pages/tickets';
import { renderFinance } from './pages/finance';
import { renderContracts, renderContractSign } from './pages/contracts';
import { initShell, buildTabBar, provideShellDeps, closeSheet, refreshContractGate, enforceContractGate } from './shell';

// ─── Durum ──────────────────────────────────────────────────────────────────
export const state: { token: string | null; user: any; settings: Record<string, string> | null } = {
  token: localStorage.getItem('lu_token'),
  user: JSON.parse(localStorage.getItem('lu_user') || 'null'),
  settings: null,
};

export function isAdmin(): boolean {
  return state.user?.role === 'admin';
}

export async function loadSettings(): Promise<Record<string, string>> {
  if (state.settings) return state.settings;
  try { const d = await api.get('/settings'); state.settings = d.settings || {}; } catch { state.settings = {}; }
  return state.settings!;
}

// ─── Oturum ─────────────────────────────────────────────────────────────────
provideShellDeps({ state, isAdmin, navigate, logout });

function login(token: string, user: any) {
  state.token = token;
  state.user = user;
  localStorage.setItem('lu_token', token);
  localStorage.setItem('lu_user', JSON.stringify(user));
  showPanel();
}

export function logout() {
  state.token = null;
  state.user = null;
  state.settings = null;
  localStorage.removeItem('lu_token');
  localStorage.removeItem('lu_user');
  showLogin();
}

function hideSplash() {
  const splash = $('#splash-screen');
  if (splash) (splash as HTMLElement).style.display = 'none';
}

function hideAllScreens() {
  hideSplash();
  ['#login-screen', '#register-screen', '#pending-screen', '#setup-screen', '#forgot-screen', '#panel'].forEach(s => {
    const el = $(s) as HTMLElement | null;
    if (el) el.style.display = 'none';
  });
}

function showLogin() { hideAllScreens(); ($('#login-screen') as HTMLElement).style.display = 'flex'; icons(); loadContact(); }
let contactLoaded = false;
async function loadContact() {
  if (contactLoaded) return; contactLoaded = true;
  try {
    const r = await fetch('/api/public/contact'); const d: any = await r.json();
    const el = $('#auth-contact');
    if (!el || (!d.phone && !d.email)) return;
    el.textContent = 'Yardım için: ';
    const add = (href: string, text: string) => { const a = document.createElement('a'); a.href = href; a.textContent = text; el.appendChild(a); };
    if (d.phone) add('tel:' + String(d.phone).replace(/[^\d+]/g, ''), String(d.phone));
    if (d.phone && d.email) el.appendChild(document.createTextNode(' · '));
    if (d.email) add('mailto:' + encodeURIComponent(String(d.email)), String(d.email));
  } catch { /* yoksay */ }
}
function showRegister() { hideAllScreens(); ($('#register-screen') as HTMLElement).style.display = 'flex'; icons(); }
function showPending() { hideAllScreens(); ($('#pending-screen') as HTMLElement).style.display = 'flex'; icons(); }
function showSetup() { hideAllScreens(); ($('#setup-screen') as HTMLElement).style.display = 'flex'; icons(); }
function showForgot() { hideAllScreens(); ($('#forgot-screen') as HTMLElement).style.display = 'flex'; icons(); }

function showPanel() {
  hideAllScreens();
  ($('#panel') as HTMLElement).style.display = 'flex';
  $('#user-name')!.textContent = state.user.name;
  $('#user-role')!.textContent = statusLabel(state.user.role);
  $('#user-avatar')!.textContent = state.user.name.charAt(0).toUpperCase();
  const headerAvatar = $('#header-user-avatar');
  if (headerAvatar) headerAvatar.textContent = state.user.name.charAt(0).toUpperCase();
  ($('#nav-admin') as HTMLElement).style.display = isAdmin() ? 'block' : 'none';
  ($('#nav-admin-reports') as HTMLElement).style.display = isAdmin() ? 'block' : 'none';
  // Sekme çubuğu rolüne göre kurulur; görünürlüğü CSS'e bırakıldı, inline stil
  // yazılmıyor — eskiden cihaz döndürülünce görünürlük yanlış kalıyordu.
  buildTabBar();
  // "Ayarlar" adı role göre değişiyor (müşteride "Bilgilerim")
  const sLabel = settingsLabel(isAdmin());
  document.querySelectorAll('.nav-item[data-page="settings"] span, #header-dropdown a[href="/settings"]').forEach(el => {
    if (el.tagName === 'SPAN') el.textContent = sLabel;
    else { const t = Array.from(el.childNodes).find(n => n.nodeType === 3); if (t) t.textContent = ' ' + sLabel; }
  });
  icons();
  navigate(location.pathname || '/dashboard');
  // İmzalanmamış sözleşme varsa portal kilitlenir.
  refreshContractGate();
}

// ─── Giriş formu ────────────────────────────────────────────────────────────
$('#login-form')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const email = ($('#login-email') as HTMLInputElement).value;
  const password = ($('#login-password') as HTMLInputElement).value;
  const btn = $('#login-btn') as HTMLButtonElement;
  hideErr('#login-error');
  if (!validateGroup(e.target as HTMLFormElement)) return;
  btn.disabled = true;
  btn.textContent = 'Giriş yapılıyor…';
  try {
    const remember = ($('#login-remember') as HTMLInputElement | null)?.checked || false;
    const data = await api.post('/auth/login', { email, password, remember });
    login(data.token, data.user);
  } catch (err: any) {
    showErr('#login-error', err.message);
  } finally {
    btn.disabled = false;
    btn.textContent = 'Giriş Yap';
  }
});

// ─── Kurulum formu ──────────────────────────────────────────────────────────
$('#setup-form')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const btn = (e.target as HTMLFormElement).querySelector('button[type="submit"]') as HTMLButtonElement | null;
  if (!validateGroup(e.target as HTMLFormElement)) return;
  if (btn) { btn.disabled = true; btn.textContent = 'Oluşturuluyor…'; }
  try {
    await api.post('/setup', {
      name: ($('#setup-name') as HTMLInputElement).value,
      email: ($('#setup-email') as HTMLInputElement).value,
      password: ($('#setup-password') as HTMLInputElement).value,
    });
    toast('Yönetici hesabı oluşturuldu. Giriş yapabilirsiniz.', 'success');
    showLogin();
    ($('#login-email') as HTMLInputElement).value = ($('#setup-email') as HTMLInputElement).value;
  } catch (err: any) {
    showErr('#setup-error', err.message);
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Yönetici Hesabı Oluştur'; }
  }
});

// ─── Ortak: hata kutusu, şifre göster/gizle, satır içi doğrulama ───────────
function showErr(id: string, msg: string) { const el = $(id) as HTMLElement; el.textContent = msg; el.classList.add('show'); }
function hideErr(id: string) { const el = $(id) as HTMLElement; el.classList.remove('show'); }

$$('.pw-toggle').forEach(btn => {
  btn.addEventListener('click', () => {
    const inp = document.getElementById(btn.getAttribute('data-pw')!) as HTMLInputElement;
    const show = inp.type === 'password';
    inp.type = show ? 'text' : 'password';
    btn.textContent = show ? 'Gizle' : 'Göster';
    btn.setAttribute('aria-label', show ? 'Şifreyi gizle' : 'Şifreyi göster');
  });
});

// Baymard: alan terk edilince doğrula, yazarken hatayı hemen kaldır
function validateField(inp: HTMLInputElement | HTMLTextAreaElement): boolean {
  const g = inp.closest('.form-group');
  if (!g) return true;
  const ok = inp.checkValidity();
  g.classList.toggle('is-invalid', !ok);
  g.classList.toggle('is-valid', ok && inp.value.trim() !== '' && inp.hasAttribute('required'));
  return ok;
}
$$('.auth-form input, .auth-form textarea').forEach(el => {
  el.addEventListener('blur', () => validateField(el as HTMLInputElement));
  el.addEventListener('input', () => { const g = el.closest('.form-group'); if (g?.classList.contains('is-invalid') && (el as HTMLInputElement).checkValidity()) g.classList.remove('is-invalid'); });
});
function validateGroup(container: Element): boolean {
  let ok = true; let first: HTMLElement | null = null;
  container.querySelectorAll('input, textarea').forEach(el => {
    const i = el as HTMLInputElement;
    if (i.type === 'radio' || i.type === 'checkbox' || (i.offsetParent === null)) return; // gizli alanları atla
    if (!validateField(i)) { ok = false; if (!first) first = i; }
  });
  if (first) (first as HTMLElement).focus();
  return ok;
}

// ─── Kayıt: 3 adım ──────────────────────────────────────────────────────────
$('#show-register')?.addEventListener('click', (e) => { e.preventDefault(); showRegister(); });
$('#show-login')?.addEventListener('click', (e) => { e.preventDefault(); showLogin(); });
$('#show-forgot')?.addEventListener('click', (e) => { e.preventDefault(); showForgot(); });
$('#forgot-back')?.addEventListener('click', (e) => { e.preventDefault(); showLogin(); });
$('#pending-back-to-login')?.addEventListener('click', () => showLogin());

const STEP_TITLES: Record<number, [string, string, string]> = {
  1: ['Hesap oluşturun', 'Yaklaşık 2 dakika. Onaylandığında projelerinizi ve faturalarınızı buradan izlersiniz.', 'Hesap'],
  2: ['Fatura bilgileri', 'Faturalarınız bu bilgilerle kesilir. Emin değilseniz sonraya bırakabilirsiniz.', 'Fatura'],
  3: ['Son kontrol', 'Bilgilerinizi gözden geçirin ve hesabınızı oluşturun.', 'Onay'],
};
function billTo(): string { return ($('input[name="bill_to"]:checked') as HTMLInputElement | null)?.value || ''; }
function goStep(n: number) {
  $$('#register-form .step').forEach(s => s.classList.toggle('active', s.getAttribute('data-step') === String(n)));
  ($('#steps-label') as HTMLElement).textContent = `Adım ${n} / 3`;
  ($('#steps-name') as HTMLElement).textContent = STEP_TITLES[n][2];
  ($('#steps-fill') as HTMLElement).style.width = (n * 33.34) + '%';
  $('.steps-bar')?.setAttribute('aria-valuenow', String(n));
  ($('#reg-title') as HTMLElement).textContent = STEP_TITLES[n][0];
  ($('#reg-lead') as HTMLElement).textContent = STEP_TITLES[n][1];
  hideErr('#register-error');
  if (n === 3) { renderSummary(); renderRegOtp(); }
  window.scrollTo({ top: 0 });
  const firstInput = $(`#register-form .step[data-step="${n}"] input:not([type=radio]):not([type=checkbox])`) as HTMLInputElement | null;
  if (n > 1 && firstInput && window.innerWidth >= 600) firstInput.focus();
}
function renderSummary() {
  const v = (id: string) => (document.getElementById(id) as HTMLInputElement).value.trim();
  const bt = billTo();
  const rows: [string, string][] = [['E-posta', v('reg-email')], ['Ad Soyad', v('reg-name')]];
  if (bt === 'business') rows.push(['Fatura', v('reg-company') + ' · VKN ' + v('reg-vkn')]);
  else if (bt === 'individual') rows.push(['Fatura', 'Şahıs' + (v('reg-tckn') ? ' · TCKN ' + v('reg-tckn') : ' · TCKN sonra')]);
  else rows.push(['Fatura', 'Sonra netleşecek']);
  const el = $('#reg-summary') as HTMLElement;
  el.innerHTML = '<strong>Özet</strong>' + rows.map(([k, val], i) => `<div class="reg-summary-row"><span>${k}</span><span>${esc(val)}<a href="#" data-goto="${i === 0 || i === 1 ? 1 : 2}">Düzenle</a></span></div>`).join('');
  el.querySelectorAll('[data-goto]').forEach(a => a.addEventListener('click', (e) => { e.preventDefault(); goStep(parseInt(a.getAttribute('data-goto')!)); }));
}
function applyBillTo() {
  const bt = billTo();
  ($('#biz-fields') as HTMLElement).style.display = bt === 'business' ? 'flex' : 'none';
  ($('#ind-fields') as HTMLElement).style.display = bt === 'individual' ? 'flex' : 'none';
  ($('#reg-company') as HTMLInputElement).required = bt === 'business';
  ($('#reg-tax-office') as HTMLInputElement).required = bt === 'business';
  ($('#reg-vkn') as HTMLInputElement).required = bt === 'business';
  $('#register-form fieldset')?.classList.remove('is-invalid');
  // Açılan ilk alanı görünür yap; masaüstünde odaklan
  const panel = bt === 'business' ? '#biz-fields' : bt === 'individual' ? '#ind-fields' : null;
  if (panel) {
    const first = $(panel + ' input') as HTMLInputElement | null;
    if (first) { first.scrollIntoView({ block: 'center' }); if (window.innerWidth >= 600) first.focus(); }
  }
}
$$('input[name="bill_to"]').forEach(r => r.addEventListener('change', applyBillTo));
// Kontrol basamağı doğrulaması (rakam dışını temizle + algoritma)
const vkn = document.getElementById('reg-vkn') as HTMLInputElement;
const tckn = document.getElementById('reg-tckn') as HTMLInputElement;
vkn?.addEventListener('input', () => { vkn.value = vkn.value.replace(/\D/g, ''); vkn.setCustomValidity(vkn.value && !isValidVKN(vkn.value) ? 'x' : ''); });
tckn?.addEventListener('input', () => { tckn.value = tckn.value.replace(/\D/g, ''); tckn.setCustomValidity(tckn.value && !isValidTCKN(tckn.value) ? 'x' : ''); });
$$('#register-form [data-next]').forEach(b => b.addEventListener('click', () => {
  const cur = b.closest('.step')!;
  if (cur.getAttribute('data-step') === '2' && !billTo()) { $('#register-form fieldset')?.classList.add('is-invalid'); return; }
  if (!validateGroup(cur)) return;
  goStep(parseInt(b.getAttribute('data-next')!));
}));
$$('#register-form [data-prev]').forEach(b => b.addEventListener('click', () => goStep(parseInt(b.getAttribute('data-prev')!))));

$('#register-form')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const form = e.target as HTMLFormElement;
  const step3 = $('#register-form .step[data-step="3"]')!;
  if (!validateGroup(step3)) return;
  if (!($('#reg-consent') as HTMLInputElement).checked) { showErr('#register-error', 'Devam etmek için aydınlatma metnini okuduğunuzu işaretleyin.'); return; }
  if (!regOtp?.verified) { showErr('#register-error', 'Devam etmeden önce telefon numaranızı doğrulayın.'); renderRegOtp(); return; }
  const btn = $('#register-btn') as HTMLButtonElement;
  hideErr('#register-error');
  btn.disabled = true;
  btn.textContent = 'Hesap oluşturuluyor…';
  try {
    const fd = new FormData(form);
    const body: any = Object.fromEntries(fd);
    body.password = body['new-password']; delete body['new-password'];
    const bt = body.bill_to; delete body.bill_to;
    body.account_type = bt === 'business' ? 'business' : 'individual';
    body.tax_id = bt === 'business' ? body.tax_id_business : (bt === 'individual' ? body.tax_id_individual : '');
    delete body.tax_id_business; delete body.tax_id_individual;
    if (bt !== 'business') { delete body.company; delete body.tax_office; }
    // Telefon sunucuda doğrulanmış challenge kaydından okunur; gövdedeki değer kullanılmaz.
    body.otp_challenge_id = regOtp!.id;
    const data = await api.post('/auth/register', body);
    if (data.pending) showPending();
    else { login(data.token, data.user); }
  } catch (err: any) {
    showErr('#register-error', err.message);
  } finally {
    btn.disabled = false;
    btn.textContent = 'Hesap Oluştur';
  }
});

// ─── Çıkış / menü ───────────────────────────────────────────────────────────
$('#dropdown-logout')?.addEventListener('click', logout);
$('#mobile-logout')?.addEventListener('click', logout);
$('#sidebar-logout')?.addEventListener('click', logout);
function setDrawer(open: boolean) {
  $('#sidebar')?.classList.toggle('open', open);
  $('#sidebar-backdrop')?.classList.toggle('open', open);
  document.body.classList.toggle('drawer-open', open);
}
$('#menu-toggle')?.addEventListener('click', () => setDrawer(!$('#sidebar')?.classList.contains('open')));
$('#bottom-nav-menu')?.addEventListener('click', (e) => { e.preventDefault(); setDrawer(true); });
$('#sidebar-backdrop')?.addEventListener('click', () => setDrawer(false));
$('#header-user-btn')?.addEventListener('click', () => {
  const dd = $('#header-dropdown') as HTMLElement;
  dd.style.display = dd.style.display === 'none' ? 'block' : 'none';
});
$('#sidebar-user-trigger')?.addEventListener('click', () => navigate('/settings'));
document.addEventListener('click', (e) => {
  const wrapper = $('#header-user-dropdown');
  if (wrapper && !wrapper.contains(e.target as Node)) ($('#header-dropdown') as HTMLElement).style.display = 'none';
});

// ─── Yönlendirici ───────────────────────────────────────────────────────────
const routes: Record<string, (params: any) => void> = {};
function route(path: string, handler: (params: any) => void) { routes[path] = handler; }

export function navigate(path: string) {
  if (path.startsWith('#')) path = path.slice(1);
  if (!path || path === '/') path = '/dashboard';
  if (location.pathname !== path) history.pushState(null, '', path);
  const content = $('#content')!;
  closeSheet();
  setBigTitle('');
  $$('.nav-item, .tab-bar a[data-page]').forEach(item => {
    const href = item.getAttribute('href');
    item.classList.toggle('active', href === path || (!!href && path.startsWith(href + '/')));
  });
  setDrawer(false);
  let matched = false;
  for (const pattern in routes) {
    const regex = new RegExp('^' + pattern.replace(/:(\w+)/g, '(?<$1>[^/]+)') + '$');
    const match = path.match(regex);
    if (match) {
      setHTML(content, '<div class="content-loading"><div class="spinner"></div></div>');
      routes[pattern](match.groups || {});
      matched = true;
      break;
    }
  }
  if (!matched) setHTML(content, '<div class="empty-state"><p>Sayfa bulunamadı</p><a href="/dashboard" class="btn btn-primary">Panele Dön</a></div>');
  enforceContractGate(path);
}

window.addEventListener('popstate', () => navigate(location.pathname));
document.addEventListener('click', (e) => {
  const link = (e.target as Element).closest('a[href^="/"]');
  if (link) {
    const href = link.getAttribute('href');
    if (href && href.startsWith('/') && !href.startsWith('/api')) { e.preventDefault(); navigate(href); }
  }
});

route('/dashboard', renderDashboard);
route('/clients', renderClients);
route('/clients/:id', (p: any) => renderClientDetail(parseInt(p.id)));
route('/projects', renderProjects);
route('/projects/:id', (p: any) => renderProjectDetail(parseInt(p.id)));
route('/invoices', renderInvoices);
route('/invoices/:id', (p: any) => renderInvoiceDetail(parseInt(p.id)));
route('/payments', renderPayments);
route('/contracts', renderContracts);
route('/contracts/:id', (p: any) => renderContractSign(parseInt(p.id)));
route('/documents', renderDocuments);
route('/tickets', renderTickets);
route('/tickets/:id', (p: any) => renderTicketDetail(parseInt(p.id)));
route('/activity', renderActivity);
route('/report', renderAnnualReport);
route('/finance', renderFinance);
route('/settings', renderSettings);

document.addEventListener('keydown', (e) => {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); openCommandPalette(); }
  if (e.key === 'Escape') {
    closeModal();
    closeCommandPalette();
    ($('#header-dropdown') as HTMLElement).style.display = 'none';
  }
});
$('#cmd-k-trigger')?.addEventListener('click', openCommandPalette);

// ─── Başlangıç ──────────────────────────────────────────────────────────────
initShell();

async function init() {
  if (state.token) {
    try {
      const data = await api.get('/auth/me');
      state.user = data.user;
      localStorage.setItem('lu_user', JSON.stringify(data.user));
      showPanel();
    } catch {
      checkSetup();
    }
  } else {
    checkSetup();
  }
}

async function checkSetup() {
  try {
    const res = await fetch('/api/setup/status');
    const data: any = await res.json();
    if (data.needs_setup) showSetup(); else showLogin();
  } catch {
    showLogin();
  }
}

init();

// ─── Kayıtta telefon doğrulama (OTP) ────────────────────────────────────────
// Kod hiçbir zaman API'den okunamaz; kullanıcı girer, sunucu doğrular.
// Kanalı hesap yönlendirmesi seçer — burada kanal seçtirmiyoruz.
// action_url dönerse kod HENÜZ gönderilmemiştir: kullanıcı WhatsApp'ta hazır
// mesajı göndermeli, kodu cevap olarak alacak. WhatsApp'ı yoksa SMS'e taşınır.

let regOtp: { id: string; verified: boolean; masked: string; actionUrl: string | null } | null = null;

function otpBox(): HTMLElement { return $('#otp-box') as HTMLElement; }

function otpSetBusy(sel: string, busy: boolean, label?: string) {
  const b = $(sel) as HTMLButtonElement | null;
  if (!b) return;
  b.disabled = busy;
  if (label) b.textContent = label;
}

function renderRegOtp() {
  const box = otpBox();
  if (!box) return;
  if (!regOtp) {
    box.innerHTML = '<p style="margin:0 0 8px">Numaranızı doğrulamamız gerekiyor.</p>'
      + '<button type="button" class="btn btn-secondary" id="otp-send">Doğrulama kodu gönder</button>';
    $('#otp-send')?.addEventListener('click', regOtpSend);
    return;
  }
  if (regOtp.verified) {
    box.innerHTML = '<p style="margin:0"><strong>' + esc(regOtp.masked) + '</strong> doğrulandı.</p>';
    return;
  }
  const wa = regOtp.actionUrl;
  box.innerHTML =
    (wa
      ? '<p style="margin:0 0 8px"><strong>Kod henüz gönderilmedi.</strong> WhatsApp\'ı açın ve hazır gelen mesajı bize gönderin; kodu cevap olarak alacaksınız.</p>'
        + '<p style="margin:0 0 10px"><a class="btn btn-primary" href="' + esc(wa) + '" target="_blank" rel="noopener noreferrer">WhatsApp\'ta aç</a></p>'
      : '<p style="margin:0 0 8px">Kod <strong>' + esc(regOtp.masked) + '</strong> numarasına gönderildi.</p>')
    + '<div class="form-group"><label for="otp-code">Doğrulama kodu</label>'
    + '<input type="text" id="otp-code" inputmode="numeric" autocomplete="one-time-code" placeholder="123456"></div>'
    + '<button type="button" class="btn btn-primary" id="otp-check">Doğrula</button> '
    + '<button type="button" class="btn btn-secondary" id="otp-sms">WhatsApp\'ım yok, SMS gönder</button>';
  $('#otp-check')?.addEventListener('click', regOtpVerify);
  $('#otp-sms')?.addEventListener('click', regOtpSms);
}

async function regOtpSend() {
  const phone = ($('#reg-phone') as HTMLInputElement).value.trim();
  if (!phone) { showErr('#register-error', 'Önce telefon numaranızı girin.'); return; }
  hideErr('#register-error');
  otpSetBusy('#otp-send', true, 'Gönderiliyor…');
  try {
    const d = await api.post('/auth/register/otp/start', { phone });
    regOtp = { id: d.challenge_id, verified: false, masked: d.masked_recipient || phone, actionUrl: d.action_url || null };
    renderRegOtp();
  } catch (err: any) {
    showErr('#register-error', err.message);
    otpSetBusy('#otp-send', false, 'Doğrulama kodu gönder');
  }
}

async function regOtpSms() {
  if (!regOtp) return;
  otpSetBusy('#otp-sms', true, 'Gönderiliyor…');
  try {
    const d = await api.post('/auth/register/otp/resend-sms', { challenge_id: regOtp.id });
    regOtp = { ...regOtp, masked: d.masked_recipient || regOtp.masked, actionUrl: null };
    renderRegOtp();
  } catch (err: any) {
    showErr('#register-error', err.message);
    otpSetBusy('#otp-sms', false, 'WhatsApp\'ım yok, SMS gönder');
  }
}

async function regOtpVerify() {
  if (!regOtp) return;
  const code = ($('#otp-code') as HTMLInputElement).value.trim();
  if (!code) { showErr('#register-error', 'Doğrulama kodunu girin.'); return; }
  hideErr('#register-error');
  otpSetBusy('#otp-check', true, 'Kontrol ediliyor…');
  try {
    const d = await api.post('/auth/register/otp/verify', { challenge_id: regOtp.id, code });
    regOtp = { ...regOtp, verified: true, masked: d.masked_recipient || regOtp.masked };
    renderRegOtp();
  } catch (err: any) {
    showErr('#register-error', err.message);
    otpSetBusy('#otp-check', false, 'Doğrula');
  }
}

// Numara değişirse önceki doğrulama düşer.
$('#reg-phone')?.addEventListener('input', () => { if (regOtp) { regOtp = null; renderRegOtp(); } });
